import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  TrendingUp,
  CheckCircle,
  Warning,
  Schedule,
  Payment,
  Autorenew,
  AssignmentTurnedIn,
  Receipt,
  PendingActions,
  Download,
  Refresh,
  Visibility,
  Person,
  AccessTime,
  Dashboard,
  BarChart,
  PieChart,
  WarningAmber,
  InfoOutlined,
  ErrorOutline,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const GILROY_MEDIUM = "GilroyMedium";
const GILROY_BOLD = "GilroyBold";
const PDF_EXPORT_WIDTH = 1280;
const PDF_EXPORT_MIN_HEIGHT = 900;

/* ─────────────────────────── Helpers ─────────────────────────── */
const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/[,\u20B9\s]/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const toINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);

const getRecordStatusDisplay = (status) => {
  const s = String(status || "").trim().toUpperCase();
  if (s === "APPROVED") return "Approved";
  if (s === "PENDING") return "Awaiting";
  if (s === "IN_REVIEW") return "In Review";
  if (s === "REJECTED") return "Rejected";
  return status || "-";
};

const getRecordStatusBadgeStyle = (status) => {
  const s = String(status || "").trim().toUpperCase();
  if (s === "APPROVED") return { color: "#fff", backgroundColor: "#47a34b" };
  if (s === "IN_REVIEW") return { color: "#ed6c02", backgroundColor: "#fff3e0" };
  if (s === "REJECTED") return { color: "#fff", backgroundColor: "#ee4646" };
  return { color: "#fff", backgroundColor: "#ec8735" };
};

/* ─────────────────────────── Pickers / Reconcile ─────────────────────────── */
function aggregateByPart(items, pick) {
  const m = new Map();
  for (const it of items) {
    const part = it.part_no || it.item_code || it.code || (it.part && it.part.code) || "";
    if (!part) continue;
    const key = String(part).trim();
    m.set(key, (m.get(key) || 0) + pick(it));
  }
  return m;
}

function poPickers(poItems) {
  return {
    qtyByPart: aggregateByPart(poItems, (it) =>
      toNum(it["qty."] ?? it.qty ?? it.quantity ?? it.parsed_qty ?? it["parsed_qty."])
    ),
    priceByPart: aggregateByPart(poItems, (it) =>
      toNum(it.parsed_rate ?? it.rate ?? it.unit_price)
    ),
    hsnByPart: (() => {
      const m = new Map();
      for (const it of poItems) {
        const part = it.part_no?.toString().trim();
        if (!part) continue;
        if (!m.has(part)) m.set(part, (it.hsn_code ?? it.hsn ?? "").toString().trim());
      }
      return m;
    })(),
  };
}

function grnPickers(grnItems) {
  return {
    qtyByPart: aggregateByPart(grnItems, (it) =>
      toNum(
        it.quantity_received ?? it.received_quantity ?? it.parsed_received_quantity ??
        it.received_q ?? it.qty ?? it.parsed_qty
      )
    ),
  };
}

function invPickers(invItems) {
  return {
    qtyByPart: aggregateByPart(invItems, (it) =>
      toNum(it.qty ?? it.quantity ?? it.received_quantity ?? it.parsed_qty ?? it.parsed_quantity)
    ),
    priceByPart: aggregateByPart(invItems, (it) =>
      toNum(it.unit_price ?? it.rate ?? it.parsed_unit_price ?? it.parsed_rate)
    ),
    hsnByPart: (() => {
      const m = new Map();
      for (const it of invItems) {
        const part = it.part_no || it.code || (it.part && it.part.code) || "";
        if (!part) continue;
        if (!m.has(part)) m.set(part, (it.hsn ?? it.hsn_code ?? "").toString().trim());
      }
      return m;
    })(),
  };
}

function reconcileRecord(rec) {
  const rj = rec.result_json || rec.extracted_data || {};
  const po = rj.po_data?.[0] ?? {};
  const grn = rj.grn_data?.[0] ?? {};
  const inv = rj.invoice_data?.[0] ?? {};
  const createdAt = rec.created_at || rec.created_on || rec.created_date || rec.createdAt || null;

  const poItems = po.line_items?.map((x) => ({
    part_no: x.part_no, qty: x.qty ?? x["qty."], "qty.": x["qty."],
    parsed_qty: x.parsed_qty ?? x["parsed_qty."], rate: x.rate,
    parsed_rate: x.parsed_rate, hsn_code: x.hsn_code,
  })) ?? [];
  const grnItems = grn.line_items?.map((x) => ({
    part_no: x.part_no ?? x.item_code,
    received_q: x.quantity_received ?? x.received_quantity ?? x.received_q,
    quantity_received: x.quantity_received,
    received_quantity: x.received_quantity ?? x.quantity_received,
    parsed_qty: x.parsed_received_quantity ?? x.parsed_qty,
  })) ?? [];
  const invItems = inv.line_items?.map((x) => ({
    part_no: x.part_no ?? x.code, qty: x.qty ?? x.quantity,
    parsed_qty: x.parsed_qty ?? x.parsed_quantity, unit_price: x.unit_price,
    parsed_unit_price: x.parsed_unit_price, hsn: x.hsn ?? x.hsn_code,
  })) ?? [];

  const { qtyByPart: poQty, priceByPart: poPrice, hsnByPart: poHSN } = poPickers(poItems);
  const { qtyByPart: grnQty } = grnPickers(grnItems);
  const { qtyByPart: invQty, priceByPart: invPrice, hsnByPart: invHSN } = invPickers(invItems);

  const poParts = new Set([...poQty.keys()]);
  const grnParts = new Set([...grnQty.keys()]);
  const invParts = new Set([...invQty.keys()]);

  const mismatches = [];
  const inc = (r) => mismatches.push(r);

  for (const p of poParts) {
    if (!grnParts.has(p)) inc(`Missing in GRN: ${p}`);
    if (!invParts.has(p)) inc(`Missing in Invoice: ${p}`);
  }
  for (const p of grnParts) if (!poParts.has(p)) inc(`Extra in GRN: ${p}`);
  for (const p of invParts) if (!poParts.has(p)) inc(`Extra in Invoice: ${p}`);

  for (const p of poParts) {
    const qPO = poQty.get(p) ?? 0, qGRN = grnQty.get(p) ?? 0, qINV = invQty.get(p) ?? 0;
    if (qPO !== qGRN || qPO !== qINV) inc(`Quantity mismatch: ${p} (PO ${qPO} vs GRN ${qGRN} vs INV ${qINV})`);
    const prPO = poPrice.get(p), prINV = invPrice.get(p);
    if (Number.isFinite(prPO) && Number.isFinite(prINV) && prPO !== prINV)
      inc(`Price mismatch: ${p} (PO ${toINR(prPO)} vs INV ${toINR(prINV)})`);
    const hPO = poHSN.get(p), hINV = invHSN.get(p);
    if (hPO && hINV && hPO !== hINV) inc(`HSN mismatch: ${p} (PO ${hPO} vs INV ${hINV})`);
  }

  const invAmount = toNum(inv.invoice_amount ?? inv.total_amount ?? inv.amount);
  const matchTimeMinutes = (() => {
    const invDateRaw = inv.invoice_date || inv.date || inv.doc_date;
    if (!invDateRaw || !createdAt) return null;
    const diff = (new Date(createdAt) - new Date(invDateRaw)) / (1000 * 60);
    return Number.isFinite(diff) && diff >= 0 ? Math.round(diff) : null;
  })();

  const hasQtyOrPriceOrItemIssue = mismatches.some((m) =>
    /Quantity mismatch|Price mismatch|Missing|Extra/.test(m)
  );
  let classification = "Fully Matched";
  if (hasQtyOrPriceOrItemIssue) classification = "With Exceptions";
  else if (mismatches.length > 0 || rec.status === "IN_REVIEW" || rec.status === "PENDING")
    classification = "Partially Matched";

  const readyForPayment = rec.status === "APPROVED" && classification !== "With Exceptions";
  const reasonBuckets = mismatches.map((m) => {
    if (m.startsWith("Quantity mismatch")) return "Quantity mismatch";
    if (m.startsWith("Price mismatch")) return "Price mismatch";
    if (m.startsWith("HSN mismatch")) return "Tax/HSN mismatch";
    if (m.startsWith("Missing")) return "Item Missing";
    if (m.startsWith("Extra")) return "Extra item";
    return "Meta mismatch";
  });

  return {
    classification, readyForPayment, invAmount, mismatches, reasonBuckets,
    createdAt, matchTimeMinutes, hasException: classification === "With Exceptions",
    matchCode: rec.match_code, status: rec.status,
  };
}

function computeKPIs(data) {
  const per = data.map(reconcileRecord);
  const total = per.length;
  const approved = per.filter((x) => x.status === "APPROVED").length;
  const fully = per.filter((x) => x.classification === "Fully Matched").length;
  const partial = per.filter((x) => x.classification === "Partially Matched").length;
  const exceptions = per.filter((x) => x.classification === "With Exceptions");
  const withExceptions = exceptions.length;
  const readyPay = per.filter((x) => x.readyForPayment).length;
  const totalExceptionValue = exceptions.reduce((s, x) => s + (x.invAmount || 0), 0);
  const now = new Date();
  const agingCount = exceptions.filter((x) => {
    if (!x.createdAt) return false;
    return (now - new Date(x.createdAt)) / (1000 * 60 * 60 * 24) > 7;
  }).length;
  const reasonCount = new Map();
  per.forEach((p) => p.reasonBuckets.forEach((r) => reasonCount.set(r, (reasonCount.get(r) || 0) + 1)));
  const top3 = [...reasonCount.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  const automated = data.filter((d) => !!(d.result_json || d.extracted_data)).length;
  const automationCoverage = total ? Math.round((automated / total) * 100) : 0;
  const delayed = per.filter((x) => x.hasException && !x.readyForPayment);
  const delayedCount = delayed.length;
  const delayedValue = delayed.reduce((s, x) => s + (x.invAmount || 0), 0);
  const manualEffortSaved = (automated / 100).toFixed(1);
  const matchTimes = per.map((p) => p.matchTimeMinutes).filter((v) => Number.isFinite(v));
  const avgMatchingTime = matchTimes.length
    ? Math.round(matchTimes.reduce((s, v) => s + v, 0) / matchTimes.length) : null;

  return {
    totals: {
      approved, total, fully, partial, withExceptions, readyPay,
      avgMatchingTime, automationCoverage, totalExceptionValue,
      agingCount, delayedCount, delayedValue, manualEffortSaved,
    },
    top3, per,
  };
}

function computeSummaryKPIs(summaryData) {
  const metrics = summaryData?.dashboard_metrics || {};
  const analytics = summaryData?.analytics_kpis || {};
  const total = Number(metrics.total_matching_done ?? summaryData?.total_count ?? 0);
  const fully = Number(metrics.fully_matched_invoices ?? 0);
  const partial = Number(metrics.partially_matched_invoices ?? 0);
  const withExceptions = Number(metrics.invoices_with_exceptions ?? 0);
  const incomplete = Number(metrics.incomplete_document_invoices ?? 0);
  const notMatched = Number(metrics.not_matched_invoices ?? 0);
  const readyPay = fully;
  const matchedPercent = Number(
    analytics.matched_or_partially_matched_percent ??
      (total ? ((fully + partial) / total) * 100 : 0)
  );
  const exceptionPercent = Number(
    analytics.invoice_exception_percent ??
      (total ? (withExceptions / total) * 100 : 0)
  );
  const monthwise = metrics.monthwise_matching_count || {};
  const per = Object.entries(monthwise).map(([yearMonth, count]) => {
    const [year, month] = String(yearMonth).split("-");
    const date = `${year}-${month || "01"}-01T00:00:00`;
    const invoices = Number(count || 0);
    return {
      createdAt: date,
      classification: "Summary",
      invoices,
      matched: Math.round((invoices * matchedPercent) / 100),
      reasonBuckets: [],
    };
  });

  return {
    totals: {
      approved: readyPay,
      total,
      fully,
      partial,
      withExceptions,
      readyPay,
      avgMatchingTime: null,
      automationCoverage: Math.round(matchedPercent || 0),
      totalExceptionValue: 0,
      agingCount: incomplete,
      delayedCount: notMatched,
      delayedValue: 0,
      manualEffortSaved: Number(analytics.average_invoices_per_matching ?? 0).toFixed(1),
      exceptionPercent,
    },
    top3: (metrics.top_3_mismatch_reasons || analytics.top_mismatch_reasons || [])
      .map((item) => [item?.reason || "Unknown", Number(item?.count || 0)]),
    per,
  };
}

/* ─────────────────────────── StatCard ─────────────────────────── */
function StatCard({ title, value, subtitle, icon, color, progress, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (loading) {
    return (
      <Card elevation={1} sx={{ height: "100%", borderRadius: 3, p: 3, bgcolor: "#f8f9fa" }}>
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={36} />
        {progress !== undefined && (
          <Box mt={2}><Skeleton variant="rounded" height={6} sx={{ borderRadius: 4 }} /></Box>
        )}
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e8edf2",
        background: "#fff",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        },
      }}
    >
      <CardContent sx={{ p: "20px !important" }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                fontFamily: GILROY_MEDIUM, fontWeight: 600,
                fontSize: isMobile ? "13px" : "14px", color: "#64748b",
                mb: 0.75, lineHeight: 1.4,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontFamily: GILROY_BOLD, fontWeight: 800,
                fontSize: isMobile ? "22px" : "26px", color: color || "#0f172a",
                lineHeight: 1.1, mb: subtitle ? 0.5 : 0,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#94a3b8" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              ml: 1.5, flexShrink: 0, width: 44, height: 44,
              borderRadius: 2.5, display: "flex", alignItems: "center",
              justifyContent: "center", bgcolor: `${color}18`,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: "1.4rem", color } })}
          </Box>
        </Box>
        {progress !== undefined && (
          <Box mt={2}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
                "& .MuiLinearProgress-bar": {
                  background: `linear-gradient(90deg, ${color}70, ${color})`,
                  borderRadius: 3,
                },
              }}
            />
            <Box display="flex" justifyContent="space-between" mt={0.75}>
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "12px", color: "#94a3b8" }}>Progress</Typography>
              <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "12px", color }}>{progress}%</Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── InvoiceTrendChart (Area style like Image 1) ─────────────────────────── */
const MONTH_ORDER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function InvoiceTrendChart({ data, loading, titleSuffix, variant = "card", exporting = false }) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = exporting ? false : isViewportMobile;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", minWidth: 160 }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "13px", mb: 0.75, color: "#334155" }}>{label}</Typography>
        {payload.map((entry) => (
          <Box key={entry.dataKey} display="flex" alignItems="center" gap={0.75} mb={0.25}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: entry.color }} />
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#475569" }}>
              {entry.name}: <strong>{entry.value}</strong>
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  };

  const content = () => {
    if (loading || !Array.isArray(data) || data.length === 0) {
      return (
        <>
          <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
        </>
      );
    }

    return (
      <>
        <Typography
          sx={{
            fontFamily: GILROY_BOLD, fontWeight: 700,
            fontSize: isMobile ? "15px" : "16px", color: "#1e293b", mb: 0.5,
          }}
        >
          Invoice Processing Trends{titleSuffix ? ` — ${titleSuffix}` : ""}
        </Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#94a3b8", mb: 2 }}>
          Total invoices vs fully matched
        </Typography>

        {/* Legend — dot style like Image 1 */}
        <Box display="flex" gap={3} mb={2} flexWrap="wrap">
          {[
            { color: "#3b82f6", label: "Monthly" },
            { color: "#22c55e", label: "Matched" },
          ].map(({ color, label }) => (
            <Box key={label} display="flex" alignItems="center" gap={0.75}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#64748b" }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, minHeight: 220 }}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="twmGradTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.03} />
                </linearGradient>
                <linearGradient id="twmGradMatched" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#94a3b8", fontFamily: GILROY_MEDIUM, fontSize: 12 }}
                tickMargin={8}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#94a3b8", fontFamily: GILROY_MEDIUM, fontSize: 12 }}
                tickMargin={4}
                width={28}
              />
              <RechartsTooltip content={<CustomTooltip />} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="invoices"
                name="Total Invoices"
                stroke="#3b82f6"
                strokeWidth={2.5}
                fill="url(#twmGradTotal)"
                dot={{ fill: "#3b82f6", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
              />
              <Area
                type="monotone"
                dataKey="matched"
                name="Fully Matched"
                stroke="#22c55e"
                strokeWidth={2.5}
                fill="url(#twmGradMatched)"
                dot={{ fill: "#22c55e", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </>
    );
  };

  const wrapperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    height: "100%",
    minHeight: 340,
    background: "#fff",
    border: "1px solid #e8edf2",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden",
  };

  if (variant === "embedded") return <Box sx={wrapperSx}>{content()}</Box>;
  return <Paper elevation={0} sx={wrapperSx}>{content()}</Paper>;
}

/* ─────────────────────────── MismatchPieChart (Image 2 style donut) ─────────────────────────── */
function MismatchPieChart({ data, loading, titleSuffix, variant = "card", exporting = false }) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = exporting ? false : isViewportMobile;
  const [chartData, setChartData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const PIE_COLORS = ["#3b82f6", "#22c55e", "#06b6d4", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#f97316"];

  useEffect(() => {
    if (!Array.isArray(data)) { setChartData([]); return; }
    const formatted = data
      .map((item) => {
        if (Array.isArray(item)) return { name: String(item[0] || "").trim() || "Unknown", value: Number(item[1]) || 0 };
        if (item && typeof item === "object") return { name: String(item.name || "").trim() || "Unknown", value: Number(item.value) || 0 };
        return null;
      })
      .filter(Boolean)
      .filter((x) => x.value > 0);
    setChartData(formatted);
  }, [data]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const total = (chartData || []).reduce((s, x) => s + x.value, 0);
    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
    return (
      <Paper elevation={4} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${item.payload.fill}40`, minWidth: 160 }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "13px", mb: 0.5 }}>{item.name}</Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#475569" }}>
          {item.value.toLocaleString()} — {pct}%
        </Typography>
      </Paper>
    );
  };

  const content = () => {
    if (loading || !chartData) {
      return (
        <>
          <Skeleton variant="text" width="60%" height={24} sx={{ mx: "auto", mb: 2 }} />
          <Skeleton variant="circular" width={160} height={160} sx={{ mx: "auto", mb: 2 }} />
          <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" width={80} height={20} sx={{ borderRadius: 4 }} />)}
          </Box>
        </>
      );
    }

    const total = chartData.reduce((s, x) => s + x.value, 0);

    return (
      <>
        <Typography
          sx={{
            fontFamily: GILROY_BOLD, fontWeight: 700,
            fontSize: isMobile ? "15px" : "16px", color: "#1e293b",
            mb: 0.5, textAlign: "center",
          }}
        >
          Mismatch Distribution{titleSuffix ? ` — ${titleSuffix}` : ""}
        </Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#94a3b8", mb: 1, textAlign: "center" }}>
          By reason type
        </Typography>

        {/* Donut chart — thick ring like Image 2 */}
        <Box sx={{ position: "relative", width: "100%", height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={82}
                innerRadius={54}
                dataKey="value"
                paddingAngle={3}
                cornerRadius={6}
                startAngle={90}
                endAngle={-270}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                    stroke="#fff"
                    strokeWidth={2}
                    opacity={activeIndex === null || activeIndex === index ? 1 : 0.65}
                  />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 9999 }} />
            </RechartsPieChart>
          </ResponsiveContainer>

          {/* Center total — like Image 2 */}
          <Box
            sx={{
              position: "absolute", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none",
            }}
          >
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "12px", color: "#94a3b8" }}>Total</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#1e293b", lineHeight: 1.1 }}>
              {total.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Bottom dot legend — Image 2 style */}
        <Box
          sx={{
            mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9",
            display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 16px",
          }}
        >
          {chartData.map((entry, index) => {
            const pct = total > 0 ? ((entry.value / total) * 100).toFixed(0) : 0;
            return (
              <Box
                key={index}
                display="flex" alignItems="center" gap={0.75}
                sx={{
                  cursor: "pointer", borderRadius: 3, px: 0.75, py: 0.25,
                  bgcolor: activeIndex === index ? "#f8fafc" : "transparent",
                  transition: "background 0.15s",
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: PIE_COLORS[index % PIE_COLORS.length], flexShrink: 0 }} />
                <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "12px", color: "#475569", whiteSpace: "nowrap" }}>
                  {entry.name} <span style={{ color: "#94a3b8" }}>({pct}%)</span>
                </Typography>
              </Box>
            );
          })}
        </Box>
      </>
    );
  };

  const wrapperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    height: "100%",
    background: "#fff",
    border: "1px solid #e8edf2",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden",
  };

  if (variant === "embedded") return <Box sx={wrapperSx}>{content()}</Box>;
  return <Paper elevation={0} sx={wrapperSx}>{content()}</Paper>;
}

/* ─────────────────────────── MismatchReasonsTable ─────────────────────────── */
function MismatchReasonsTable({ data, loading }) {
  const theme = useTheme();

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e8edf2", height: "100%" }}>
        <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
        {[1, 2, 3].map((i) => (
          <Box key={i} display="flex" justifyContent="space-between" mb={2}>
            <Skeleton variant="rounded" width="55%" height={24} />
            <Skeleton variant="rounded" width="15%" height={24} />
            <Skeleton variant="rounded" width="15%" height={24} />
          </Box>
        ))}
      </Paper>
    );
  }

  const totalMismatches = data.reduce((s, [, c]) => s + c, 0);

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e8edf2", height: "100%" }}>
      <Box display="flex" alignItems="center" mb={2.5}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 700, fontSize: "16px", color: "#1e293b", flex: 1 }}>
          Top Mismatch Reasons
        </Typography>
        <Tooltip
          title={`Based on ${totalMismatches} total mismatches`}
          slotProps={{ tooltip: { sx: { fontFamily: GILROY_MEDIUM, fontSize: "13px" } } }}
        >
          <IconButton size="small" sx={{ color: "#94a3b8" }}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ "& th": { py: 1.25, px: 1.5, borderBottom: "2px solid #f1f5f9" } }}>
            {["Reason", "Count", "%"].map((h, i) => (
              <TableCell
                key={i}
                align={i === 0 ? "left" : "right"}
                sx={{ fontFamily: GILROY_BOLD, fontSize: "13px", fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#94a3b8", fontFamily: GILROY_MEDIUM, fontSize: "14px", fontStyle: "italic" }}>
                No mismatches found
              </TableCell>
            </TableRow>
          ) : (
            data.map(([reason, count], idx) => {
              const pct = totalMismatches > 0 ? Math.round((count / totalMismatches) * 100) : 0;
              const barColors = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];
              const barColor = barColors[idx % barColors.length];
              return (
                <TableRow
                  key={idx}
                  sx={{
                    "&:hover": { bgcolor: "#f8fafc" },
                    "& td": { py: 1.5, px: 1.5, borderBottom: "1px solid #f8fafc" },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#334155", mb: 0.5 }}>
                        {reason}
                      </Typography>
                      {/* Mini progress bar */}
                      <Box sx={{ height: 3, borderRadius: 2, bgcolor: "#f1f5f9", overflow: "hidden", maxWidth: 180 }}>
                        <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: barColor, borderRadius: 2, transition: "width 0.4s" }} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", color: "#334155" }}>{count}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${pct}%`}
                      size="small"
                      sx={{
                        fontFamily: GILROY_BOLD, fontSize: "12px", fontWeight: 700,
                        height: 22, bgcolor: `${barColor}15`, color: barColor,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {totalMismatches > 0 && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2 }}>
          <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "12px", color: "#94a3b8", fontStyle: "italic" }}>
            Percentages are relative to {totalMismatches} total mismatches across all records.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

/* ─────────────────────────── RecordsSnapshot ─────────────────────────── */
function RecordsSnapshot({ data, per, loading, onViewDetails, exporting = false }) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = exporting ? false : isViewportMobile;

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e8edf2" }}>
        <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} display="flex" justifyContent="space-between" mb={2}>
            {[1, 2, 3, 4, 5].map((j) => <Skeleton key={j} variant="rounded" width="15%" height={28} />)}
          </Box>
        ))}
      </Paper>
    );
  }

  const statusChipSx = (status) => {
    const s = String(status || "").toUpperCase();
    const map = {
      APPROVED: { bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" },
      IN_REVIEW: { bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" },
      REJECTED:  { bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" },
    };
    return map[s] || { bgcolor: "#fff7ed", color: "#d97706", border: "1px solid #fde68a" };
  };

  const classChipSx = (cls) => {
    if (cls === "Fully Matched") return { bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" };
    if (cls === "Partially Matched") return { bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" };
    return { bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" };
  };

  const sorted = [...data]
    .map((item, i) => ({ rec: item, p: per[i] }))
    .sort((a, b) => {
      const numA = parseInt((a.rec.match_code || "").split("/").pop()) || 0;
      const numB = parseInt((b.rec.match_code || "").split("/").pop()) || 0;
      return numB - numA;
    })
    .slice(0, 5);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8edf2", overflow: "hidden" }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, borderBottom: "1px solid #f1f5f9" }}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
          Recent Invoices
        </Typography>
        <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#94a3b8", mt: 0.25 }}>
          Latest 5 records
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "#f8fafc",
                "& th": {
                  py: 1.5, px: { xs: 1.5, sm: 2 },
                  borderBottom: "1px solid #e8edf2",
                  fontFamily: GILROY_BOLD, fontSize: "13px",
                  fontWeight: 700, color: "#64748b", whiteSpace: "nowrap",
                },
              }}
            >
              {["Match Code", "Status", "Classification", "Amount", "Mismatches", ""].map((h, i) => (
                <TableCell key={i}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#94a3b8", fontFamily: GILROY_MEDIUM, fontStyle: "italic", fontSize: "14px" }}>
                  No records available
                </TableCell>
              </TableRow>
            ) : (
              sorted.map(({ rec, p }) => {
                const rowSummary = p || {
                  classification: "With Exceptions",
                  invAmount: 0,
                  mismatches: [],
                };

                return (
                <TableRow
                  key={rec.token || rec.match_code}
                  hover
                  sx={{
                    "&:hover": { bgcolor: "#f8fafc" },
                    "& td": {
                      py: 1.75, px: { xs: 1.5, sm: 2 },
                      borderBottom: "1px solid #f8fafc",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", color: "#334155", fontWeight: 700 }}>
                      {rec.match_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getRecordStatusDisplay(rec.status)}
                      size="small"
                      sx={{
                        fontFamily: GILROY_MEDIUM, fontSize: "12px", fontWeight: 600,
                        height: 26, borderRadius: 1.5, ...statusChipSx(rec.status),
                        "& .MuiChip-label": { px: 1.25 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rowSummary.classification}
                      size="small"
                      sx={{
                        fontFamily: GILROY_MEDIUM, fontSize: "12px", fontWeight: 600,
                        height: 26, borderRadius: 1.5, ...classChipSx(rowSummary.classification),
                        "& .MuiChip-label": { px: 1.25 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "14px", color: "#334155" }}>
                      {toINR(rowSummary.invAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: GILROY_MEDIUM, fontSize: "13px",
                        color: rowSummary.mismatches.length ? "#ef4444" : "#94a3b8",
                      }}
                    >
                      {rowSummary.mismatches.length > 0
                        ? rowSummary.mismatches[0].replace(/:.+$/, "").trim()
                        : "—"}
                      {rowSummary.mismatches.length > 1 ? ` +${rowSummary.mismatches.length - 1}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View details" slotProps={{ tooltip: { sx: { fontFamily: GILROY_MEDIUM, fontSize: "13px" } } }}>
                      <IconButton
                        size="small"
                        onClick={() => onViewDetails(rec, rowSummary)}
                        sx={{
                          width: 32, height: 32, borderRadius: 1.5,
                          bgcolor: "#f1f5f9", color: "#64748b",
                          "&:hover": { bgcolor: theme.palette.primary.main, color: "#fff" },
                          transition: "all 0.15s",
                        }}
                      >
                        <Visibility sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/* ─────────────────────────── DetailDialog ─────────────────────────── */
function DetailDialog({ open, onClose, record, reconciliation }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  if (!record || !reconciliation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: isMobile ? 0.5 : 1 } }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #f1f5f9", py: isMobile ? 2 : 2.5, px: isMobile ? 2 : 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: isMobile ? "16px" : "18px", color: "#1e293b" }}>
            {record.match_code}
          </Typography>
          <Chip
            label={reconciliation.classification}
            size="small"
            sx={{
              fontFamily: GILROY_MEDIUM, fontSize: "13px", fontWeight: 600, height: 28,
              bgcolor: reconciliation.classification === "Fully Matched" ? "#f0fdf4" :
                       reconciliation.classification === "Partially Matched" ? "#fff7ed" : "#fef2f2",
              color: reconciliation.classification === "Fully Matched" ? "#16a34a" :
                     reconciliation.classification === "Partially Matched" ? "#c2410c" : "#dc2626",
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ py: 3, px: isMobile ? 2 : 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#64748b", mb: 0.5 }}>Status</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "16px", color: "#1e293b" }}>{record.status}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#64748b", mb: 0.5 }}>Invoice Amount</Typography>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#3b82f6" }}>{toINR(reconciliation.invAmount)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#64748b", mb: 1 }}>Mismatches</Typography>
            {reconciliation.mismatches.length > 0 ? (
              <Box sx={{ border: "1px solid #fecaca", borderRadius: 2, bgcolor: "#fff5f5", p: 2 }}>
                {reconciliation.mismatches.map((m, i) => (
                  <Box key={i} display="flex" alignItems="flex-start" gap={1} mb={i < reconciliation.mismatches.length - 1 ? 1 : 0}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#ef4444", mt: "6px", flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#334155", lineHeight: 1.6 }}>{m}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", fontStyle: "italic" }}>
                No mismatches found
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: isMobile ? 2 : 3, py: 2, borderTop: "1px solid #f1f5f9" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ borderRadius: 2, fontFamily: GILROY_BOLD, fontSize: "14px", textTransform: "none", px: 4, py: 1 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─────────────────────────── Skeleton / Error ─────────────────────────── */
function LoadingSkeleton() {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="35%" height={36} sx={{ mb: 3 }} />
      <Grid container spacing={2.5}>
        {[...Array(8)].map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function ErrorDisplay({ message, onRetry }) {
  return (
    <Box sx={{ p: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <ErrorOutline sx={{ fontSize: 56, color: "#ef4444", mb: 2, opacity: 0.8 }} />
      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#dc2626", mb: 1 }}>Something went wrong</Typography>
      <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#64748b", mb: 3, maxWidth: 480 }}>{message}</Typography>
      <Button variant="contained" onClick={onRetry} startIcon={<Refresh />} sx={{ borderRadius: 2, fontFamily: GILROY_BOLD, textTransform: "none" }}>
        Try Again
      </Button>
    </Box>
  );
}

/* ─────────────────────────── Year / Month helpers ─────────────────────────── */
function getAvailableYears(per = [], fallbackYear = new Date().getFullYear()) {
  const years = new Set();
  per.forEach((p) => {
    if (!p?.createdAt) return;
    const d = new Date(p.createdAt);
    if (!Number.isNaN(d.getTime())) years.add(d.getFullYear());
  });
  const sorted = [...years].sort((a, b) => a - b);
  return sorted.length ? sorted : [fallbackYear];
}

function filterPerByYearMonth(per = [], year, month) {
  if (!Array.isArray(per) || !year) return [];
  const y = Number(year);
  const monthIndex = month === "all" ? null : Number(month);
  return per.filter((p) => {
    if (!p?.createdAt) return false;
    const d = new Date(p.createdAt);
    if (Number.isNaN(d.getTime())) return false;
    if (d.getFullYear() !== y) return false;
    return monthIndex === null || d.getMonth() === monthIndex;
  });
}

function buildInvoiceTrendSeries(per = [], year, month, now = new Date()) {
  if (!year) return [];
  const y = Number(year);
  const isAllMonths = month === "all";
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();

  if (!isAllMonths) {
    const monthIndex = Number(month);
    if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return [];
    const daysInMonth = new Date(y, monthIndex + 1, 0).getDate();
    const limitDays = y === currentYear && monthIndex === currentMonthIndex
      ? Math.min(daysInMonth, now.getDate()) : daysInMonth;
    const byDay = new Map();
    per.forEach((p) => {
      if (!p?.createdAt) return;
      const d = new Date(p.createdAt);
      if (Number.isNaN(d.getTime()) || d.getFullYear() !== y || d.getMonth() !== monthIndex) return;
      const key = String(d.getDate()).padStart(2, "0");
      const curr = byDay.get(key) || { name: key, invoices: 0, matched: 0 };
      const invoiceCount = Number(p.invoices ?? 1);
      curr.invoices += Number.isFinite(invoiceCount) ? invoiceCount : 1;
      if (p.matched != null) {
        const matchedCount = Number(p.matched);
        curr.matched += Number.isFinite(matchedCount) ? matchedCount : 0;
      } else if (p.classification === "Fully Matched") curr.matched += 1;
      byDay.set(key, curr);
    });
    return Array.from({ length: limitDays }, (_, i) => {
      const key = String(i + 1).padStart(2, "0");
      return byDay.get(key) || { name: key, invoices: 0, matched: 0 };
    });
  }

  let latestCapturedMonth = -1;
  if (y === currentYear) {
    per.forEach((p) => {
      if (!p?.createdAt) return;
      const d = new Date(p.createdAt);
      if (!Number.isNaN(d.getTime()) && d.getFullYear() === y)
        latestCapturedMonth = Math.max(latestCapturedMonth, d.getMonth());
    });
  }
  const maxMonthsToShow = y === currentYear
    ? Math.min(currentMonthIndex, latestCapturedMonth >= 0 ? latestCapturedMonth : currentMonthIndex) + 1
    : 12;

  const byMonth = new Map();
  per.forEach((p) => {
    if (!p?.createdAt) return;
    const d = new Date(p.createdAt);
    if (Number.isNaN(d.getTime()) || d.getFullYear() !== y) return;
    const monthName = MONTH_ORDER[d.getMonth()];
    const curr = byMonth.get(monthName) || { name: monthName, invoices: 0, matched: 0 };
    const invoiceCount = Number(p.invoices ?? 1);
    curr.invoices += Number.isFinite(invoiceCount) ? invoiceCount : 1;
    if (p.matched != null) {
      const matchedCount = Number(p.matched);
      curr.matched += Number.isFinite(matchedCount) ? matchedCount : 0;
    } else if (p.classification === "Fully Matched") curr.matched += 1;
    byMonth.set(monthName, curr);
  });

  return MONTH_ORDER.slice(0, maxMonthsToShow).map(
    (m) => byMonth.get(m) || { name: m, invoices: 0, matched: 0 }
  );
}

/* ─────────────────────────── Main Dashboard ─────────────────────────── */
export default function TWMOverviewDashboard({
  data = [],
  summaryData = null,
  loading = false,
  onExportDashboard,
}) {
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(loading || !data.length);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const isMobile = exporting ? false : isViewportMobile;
  const dashboardRef = useRef(null);
  const now = useMemo(() => new Date(), []);

  const [detailDialog, setDetailDialog] = useState({ open: false, record: null, reconciliation: null });

  const { totals, per } = useMemo(() => {
    try {
      return summaryData?.dashboard_metrics
        ? computeSummaryKPIs(summaryData)
        : computeKPIs(data);
    }
    catch (err) { setError(err.message); return { totals: {}, per: [] }; }
  }, [data, summaryData]);

  const recordPer = useMemo(() => {
    try {
      return computeKPIs(data).per;
    } catch {
      return [];
    }
  }, [data]);

  const availableYears = useMemo(() => getAvailableYears(per, now.getFullYear()), [per, now]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    if (!availableYears.length) return;
    const latest = availableYears[availableYears.length - 1];
    if (!selectedYear || !availableYears.includes(selectedYear)) setSelectedYear(latest);
  }, [availableYears, selectedYear]);

  const maxMonthInDataForSelectedYear = useMemo(() => {
    if (!selectedYear) return -1;
    const y = Number(selectedYear);
    let max = -1;
    per.forEach((p) => {
      if (!p?.createdAt) return;
      const d = new Date(p.createdAt);
      if (!Number.isNaN(d.getTime()) && d.getFullYear() === y) max = Math.max(max, d.getMonth());
    });
    return max;
  }, [per, selectedYear]);

  const maxMonthIndexForYear = useMemo(() => {
    if (!selectedYear) return 11;
    const yearNum = Number(selectedYear);
    if (yearNum !== now.getFullYear()) return 11;
    const latestCaptured = maxMonthInDataForSelectedYear >= 0 ? maxMonthInDataForSelectedYear : now.getMonth();
    return Math.min(now.getMonth(), latestCaptured);
  }, [selectedYear, now, maxMonthInDataForSelectedYear]);

  useEffect(() => {
    if (selectedMonth === "all") return;
    const idx = Number(selectedMonth);
    if (!Number.isFinite(idx) || idx < 0 || idx > maxMonthIndexForYear) setSelectedMonth("all");
  }, [selectedMonth, maxMonthIndexForYear]);

  const monthOptions = useMemo(
    () => MONTH_ORDER.slice(0, maxMonthIndexForYear + 1).map((label, idx) => ({ value: idx, label })),
    [maxMonthIndexForYear]
  );

  const filteredPerForCharts = useMemo(
    () => filterPerByYearMonth(per, selectedYear, selectedMonth),
    [per, selectedYear, selectedMonth]
  );

  const trendSeries = useMemo(
    () => buildInvoiceTrendSeries(per, selectedYear, selectedMonth, now),
    [per, selectedYear, selectedMonth, now]
  );

  const trendTitleSuffix = useMemo(() => {
    if (!selectedYear) return "";
    if (selectedMonth === "all") return String(selectedYear);
    return `${MONTH_ORDER[Number(selectedMonth)] || ""} ${selectedYear}`;
  }, [selectedYear, selectedMonth]);

  const mismatchReasonEntries = useMemo(() => {
    const summaryReasons =
      summaryData?.dashboard_metrics?.top_3_mismatch_reasons ||
      summaryData?.analytics_kpis?.top_mismatch_reasons;

    if (Array.isArray(summaryReasons) && summaryReasons.length > 0) {
      return summaryReasons
        .map((item) => [item?.reason || "Unknown", Number(item?.count || 0)])
        .sort((a, b) => b[1] - a[1]);
    }

    const reasonCount = new Map();
    filteredPerForCharts.forEach((p) => {
      (p?.reasonBuckets || []).forEach((r) => {
        if (!r) return;
        reasonCount.set(r, (reasonCount.get(r) || 0) + 1);
      });
    });
    return [...reasonCount.entries()].sort((a, b) => b[1] - a[1]);
  }, [filteredPerForCharts, summaryData]);

  const mismatchDonutData = useMemo(() => {
    const MAX_SLICES = 6;
    const top = mismatchReasonEntries.slice(0, MAX_SLICES);
    const rest = mismatchReasonEntries.slice(MAX_SLICES);
    const otherCount = rest.reduce((s, [, c]) => s + c, 0);
    return otherCount > 0 ? [...top, ["Other", otherCount]] : top;
  }, [mismatchReasonEntries]);

  const mismatchTableData = useMemo(() => mismatchReasonEntries.slice(0, 6), [mismatchReasonEntries]);

  useEffect(() => {
    setIsLoading(loading);
    if (!loading && (data.length || summaryData?.dashboard_metrics)) setError(null);
  }, [data, loading, summaryData]);

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
  };

  /* PDF Export — unchanged from original */
  const exportDashboardAsPDF = async () => {
    const exportRoot = dashboardRef.current;
    if (!exportRoot) return;
    const renderOptions = {
      scale: 2, useCORS: true, allowTaint: false, backgroundColor: "#ffffff",
      logging: false, scrollX: 0, scrollY: 0,
      ignoreElements: (el) => !!(el?.hasAttribute?.("data-export-ignore") || el?.getAttribute?.("data-export-ignore") === "true"),
      onclone: (clonedDoc) => {
        const clonedRoot = clonedDoc.querySelector('[data-dashboard-export-root="true"]');
        if (clonedRoot) {
          clonedRoot.style.width = `${PDF_EXPORT_WIDTH}px`;
          clonedRoot.style.maxWidth = "none";
          clonedRoot.style.overflow = "visible";
        }
        clonedDoc.documentElement.style.width = `${PDF_EXPORT_WIDTH}px`;
        clonedDoc.body.style.width = `${PDF_EXPORT_WIDTH}px`;
      },
    };
    const pdf = new jsPDF("p", "mm", "a4");
    const margin = 8;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;
    const addCanvasToPdf = (canvas) => {
      if (!canvas?.width || !canvas?.height) return false;
      const pageHeightPx = Math.max(1, Math.floor((usableHeight * canvas.width) / usableWidth));
      const sliceCanvas = document.createElement("canvas");
      const sliceCtx = sliceCanvas.getContext("2d");
      if (!sliceCtx) return false;
      let sourceY = 0, hasRendered = false;
      while (sourceY < canvas.height) {
        const sliceH = Math.min(pageHeightPx, canvas.height - sourceY);
        sliceCanvas.width = canvas.width; sliceCanvas.height = sliceH;
        sliceCtx.fillStyle = "#fff";
        sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        sliceCtx.drawImage(canvas, 0, sourceY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        let imgData = "";
        try { imgData = sliceCanvas.toDataURL("image/jpeg", 0.95); } catch { return hasRendered; }
        if (typeof imgData !== "string" || !imgData.startsWith("data:image/")) return hasRendered;
        const imgH = (sliceH * usableWidth) / canvas.width;
        if (!Number.isFinite(imgH) || imgH <= 0) return hasRendered;
        if (hasRendered) pdf.addPage();
        pdf.addImage(imgData, "JPEG", margin, margin, usableWidth, imgH);
        hasRendered = true; sourceY += sliceH;
      }
      return hasRendered;
    };
    try {
      setExporting(true);
      await new Promise((r) => setTimeout(r, 250));
      try { if (document.fonts?.ready) await document.fonts.ready; } catch (_) {}
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const sections = Array.from(exportRoot.querySelectorAll("[data-export-section]"));
      if (sections.length > 0) {
        let firstPage = true;
        for (const node of sections) {
          const w = Math.max(PDF_EXPORT_WIDTH, Math.ceil(node.scrollWidth || node.offsetWidth || 0));
          const h = Math.max(1, Math.ceil(node.scrollHeight || node.offsetHeight || 0));
          const canvas = await html2canvas(node, { ...renderOptions, width: w, height: h, windowWidth: Math.max(w, PDF_EXPORT_WIDTH), windowHeight: Math.max(h, PDF_EXPORT_MIN_HEIGHT) });
          const imgH = (canvas.height * usableWidth) / canvas.width;
          let imgData = ""; try { imgData = canvas.toDataURL("image/jpeg", 0.95); } catch { continue; }
          if (!imgData.startsWith("data:image/") || !Number.isFinite(imgH) || imgH <= 0) continue;
          if (!firstPage) pdf.addPage();
          pdf.addImage(imgData, "JPEG", margin, margin, usableWidth, Math.min(imgH, usableHeight));
          firstPage = false;
        }
      } else {
        const rw = Math.max(PDF_EXPORT_WIDTH, Math.ceil(exportRoot.scrollWidth || exportRoot.offsetWidth || 0));
        const rh = Math.max(1, Math.ceil(exportRoot.scrollHeight || exportRoot.offsetHeight || 0));
        const canvas = await html2canvas(exportRoot, { ...renderOptions, width: rw, height: rh, windowWidth: Math.max(rw, PDF_EXPORT_WIDTH), windowHeight: Math.max(rh, PDF_EXPORT_MIN_HEIGHT) });
        addCanvasToPdf(canvas);
      }
      pdf.save("Three-Way-Matching-Dashboard.pdf");
    } catch (e) { console.error("Dashboard PDF export failed:", e); }
    finally { setExporting(false); }
  };

  const handleViewDetails = (record, reconciliation) => setDetailDialog({ open: true, record, reconciliation });
  const handleCloseDetails = () => setDetailDialog({ open: false, record: null, reconciliation: null });

  if (isLoading && !data.length) return <LoadingSkeleton />;
  if (error) return <ErrorDisplay message={error} onRetry={() => window.location.reload()} />;

  /* ─── Compact alert card ─── */
  const AlertCard = ({ title, count, value, icon: Icon, accent, detail }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5, borderRadius: 3, border: "1px solid #e8edf2", height: "100%",
        position: "relative", overflow: "hidden",
        "&:before": {
          content: '""', position: "absolute", top: 0, left: 0, right: 0,
          height: 3, background: accent,
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "15px", color: "#1e293b", lineHeight: 1.3, maxWidth: "70%" }}>
          {title}
        </Typography>
        <Chip
          label={count}
          size="small"
          sx={{
            fontFamily: GILROY_BOLD, fontSize: "13px", fontWeight: 700,
            height: 26, bgcolor: `${accent.split(",")[0].replace("linear-gradient(90deg, ", "")}20`,
            color: accent.split(",")[0].replace("linear-gradient(90deg, ", ""),
            "& .MuiChip-label": { px: 1.25 },
          }}
        />
      </Box>
      <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#64748b", mb: value ? 1.5 : 0 }}>{detail}</Typography>
      {value && (
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: "20px", color: "#1e293b" }}>{value}</Typography>
      )}
    </Paper>
  );

  return (
    <Box
      sx={{
        width: "100%",          // ← key fix: fill parent width, not 100vw
        minWidth: 0,            // ← allow shrinking with sidebar
        bgcolor: "#f4f6f8",
        pt: 3,
      }}
    >
      <Box
        component="main"
        ref={dashboardRef}
        data-dashboard-export-root="true"
        sx={{
          width: "100%",
          minWidth: 0,
          px: { xs: 2, sm: 3 },
          pb: 4,
          ...(exporting ? { width: `${PDF_EXPORT_WIDTH}px`, minWidth: `${PDF_EXPORT_WIDTH}px`, overflow: "visible" } : {}),
        }}
      >
        {/* ── Header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          mb={3.5}
          data-export-section
        >
          <Box>
            <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 800, fontSize: isMobile ? "20px" : "24px", color: "#1e293b" }}>
              P2P Compliance & Matching Dashboard
            </Typography>
            <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px", color: "#94a3b8", mt: 0.5 }}>
              Monitor and analyze invoice matching performance
            </Typography>
          </Box>
          <Box display="flex" gap={1.5} mt={isMobile ? 0 : 0} data-export-ignore>
            <Button
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: "18px !important" }} />}
              onClick={handleRefresh}
              size="medium"
              sx={{
                borderRadius: 2, fontFamily: GILROY_MEDIUM, fontSize: "14px",
                textTransform: "none", borderColor: "#e2e8f0", color: "#475569",
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download sx={{ fontSize: "18px !important" }} />}
              onClick={exportDashboardAsPDF}
              size="medium"
              disabled={!data.length || exporting}
              sx={{
                borderRadius: 2, fontFamily: GILROY_BOLD, fontSize: "14px",
                textTransform: "none", boxShadow: "none",
                "&:hover": { boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
              }}
            >
              {exporting ? "Exporting…" : "Export PDF"}
            </Button>
          </Box>
        </Box>

        {/* ── KPI Row 1 ── */}
        <Grid container spacing={2} mb={2.5} data-export-section>
          {[
            { title: "Total Invoices Processed", value: totals.total, icon: <TrendingUp />, color: "#3b82f6" },
            { title: "Fully Matched", value: totals.fully, subtitle: `${Math.round(totals.total ? (totals.fully / totals.total) * 100 : 0)}% of total`, icon: <CheckCircle />, color: "#22c55e" },
            { title: "Partially Matched", value: totals.partial, icon: <Autorenew />, color: "#f59e0b" },
            { title: "With Exceptions", value: totals.withExceptions, icon: <Warning />, color: "#ef4444" },
          ].map((card, i) => (
            <Grid item xs={6} sm={6} md={3} key={i} sx={{ minWidth: 0 }}>
              <StatCard {...card} loading={isLoading} />
            </Grid>
          ))}
        </Grid>

        {/* ── KPI Row 2 ── */}
        <Grid container spacing={2} mb={2.5} data-export-section>
          {[
            { title: "Ready for Payment", value: totals.approved, icon: <Payment />, color: "#06b6d4" },
            { title: "Avg Matching Time", value: totals.avgMatchingTime != null ? `${totals.avgMatchingTime} min` : "N/A", icon: <Schedule />, color: "#8b5cf6" },
            { title: "Automation Coverage", value: `${totals.automationCoverage}%`, icon: <AssignmentTurnedIn />, color: "#10b981", progress: totals.automationCoverage },
            { title: "Total Exception Value", value: toINR(totals.totalExceptionValue), icon: <ErrorOutline />, color: "#f97316" },
          ].map((card, i) => (
            <Grid item xs={6} sm={6} md={3} key={i} sx={{ minWidth: 0 }}>
              <StatCard {...card} loading={isLoading} />
            </Grid>
          ))}
        </Grid>

        {/* ── KPI Row 3 ── */}
        <Grid container spacing={2} mb={3} data-export-section>
          {[
            { title: "Manual Effort Saved", value: `${totals.manualEffortSaved} FTEs/mo`, icon: <Person />, color: "#6366f1" },
            { title: "Aging > 7 Days", value: totals.agingCount, icon: <AccessTime />, color: "#dc2626" },
            { title: "SLA Breach Risk", value: totals.delayedCount, subtitle: `${toINR(totals.delayedValue)} value`, icon: <PendingActions />, color: "#c2410c" },
            {
              title: "Exception Resolution Rate",
              value: totals.withExceptions > 0
                ? `${Math.round(((totals.withExceptions - totals.agingCount) / totals.withExceptions) * 100)}%`
                : "100%",
              subtitle: "Resolved within 7 days",
              icon: <CheckCircle />,
              color: "#16a34a",
            },
          ].map((card, i) => (
            <Grid item xs={6} sm={6} md={3} key={i} sx={{ minWidth: 0 }}>
              <StatCard {...card} loading={isLoading} />
            </Grid>
          ))}
        </Grid>

        {/* ── Trends & Mismatch Charts ── */}
        <Paper
          data-export-section
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3,
            border: "1px solid #e8edf2", bgcolor: "#fff",
          }}
        >
          {/* Filter row */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            mb={2.5}
          >
            <Box>
              <Typography sx={{ fontFamily: GILROY_BOLD, fontWeight: 700, fontSize: "16px", color: "#1e293b" }}>
                Trends & Mismatch Analysis
              </Typography>
              <Typography sx={{ fontFamily: GILROY_MEDIUM, fontSize: "13px", color: "#94a3b8", mt: 0.25 }}>
                Filter insights by year and month
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" data-export-ignore="true">
              <TextField
                select
                label="Year"
                size="small"
                value={selectedYear ?? ""}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setSelectedYear(Number.isFinite(y) ? y : null);
                  setSelectedMonth("all");
                }}
                sx={{
                  minWidth: 120,
                  "& .MuiOutlinedInput-root": { borderRadius: 2, fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                  "& .MuiInputLabel-root": { fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                }}
              >
                {availableYears.map((y) => (
                  <MenuItem key={y} value={y} sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px" }}>{y}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Month"
                size="small"
                value={selectedMonth}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "all") { setSelectedMonth("all"); return; }
                  const idx = Number(v);
                  setSelectedMonth(Number.isFinite(idx) ? idx : "all");
                }}
                sx={{
                  minWidth: 140,
                  "& .MuiOutlinedInput-root": { borderRadius: 2, fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                  "& .MuiInputLabel-root": { fontFamily: GILROY_MEDIUM, fontSize: "14px" },
                }}
              >
                <MenuItem value="all" sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px" }}>All Months</MenuItem>
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value} sx={{ fontFamily: GILROY_MEDIUM, fontSize: "14px" }}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>

          {/* Charts — side by side, collapse to stacked on narrow screens */}
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
              <InvoiceTrendChart
                data={trendSeries}
                loading={isLoading}
                titleSuffix={trendTitleSuffix}
                variant="embedded"
                exporting={exporting}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
              <MismatchPieChart
                data={mismatchDonutData}
                loading={isLoading}
                titleSuffix={trendTitleSuffix}
                variant="embedded"
                exporting={exporting}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ── Mismatch Table + Alert Cards ── */}
        <Grid container spacing={2.5} mb={3} data-export-section>
          <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
            <MismatchReasonsTable data={mismatchTableData} loading={isLoading} />
          </Grid>
          <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
            <Stack spacing={2.5} height="100%">
              <AlertCard
                title="Aging Exceptions"
                count={`${totals.agingCount} cases`}
                detail="Cases pending beyond 7 days"
                accent="linear-gradient(90deg, #ef4444, #f87171)"
              />
              <AlertCard
                title="SLA Breach Risk Payments"
                count={`${totals.delayedCount} invoices`}
                detail="Due to unresolved mismatches"
                value={toINR(totals.delayedValue)}
                accent="linear-gradient(90deg, #f59e0b, #fbbf24)"
              />
            </Stack>
          </Grid>
        </Grid>

        {/* ── Recent Invoices ── */}
        <Box data-export-section>
          <RecordsSnapshot
            data={data}
            per={recordPer}
            loading={isLoading}
            onViewDetails={handleViewDetails}
            exporting={exporting}
          />
        </Box>

        {/* ── Detail Dialog ── */}
        <DetailDialog
          open={detailDialog.open}
          onClose={handleCloseDetails}
          record={detailDialog.record}
          reconciliation={detailDialog.reconciliation}
        />
      </Box>
    </Box>
  );
}
